import React from "react";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { Panel } from "../../../ui";
import { C } from "../../../common/theme";
import { PipelineStage, STATUS_COLORS, formatCount, formatMoney } from "../salesData";
import { formatDuration } from "../../../common/utils/format";

interface OrderPipelineProps {
  stages: PipelineStage[];
  selected: string[];
  onToggle: (status: string) => void;
  currency: string;
}

const ALWAYS_SHOWN = ["Pending Confirmation", "Pending Delivery", "In Delivery", "Completed"];

/** Where every item in the period sits in the order lifecycle. Clicking a
 * stage filters the page to it. */
export default function OrderPipeline({ stages, selected, onToggle, currency }: OrderPipelineProps) {
  const total = stages.reduce((acc, s) => acc + s.items, 0);
  const visible = stages.filter((s) => s.items > 0 || ALWAYS_SHOWN.includes(s.status));

  return (
    <Panel sx={{ height: "100%", boxSizing: "border-box" }}>
      <Typography variant="h6" component="h3" sx={{ fontSize: 15 }}>
        Order pipeline
      </Typography>
      <Typography variant="caption" sx={{ color: C.muted }}>
        {formatCount(total)} items in this period · tap a stage to filter
      </Typography>

      {total > 0 && (
        <Box sx={{ display: "flex", gap: "2px", height: 10, my: 1.5 }} role="img" aria-label="Share of items by status">
          {stages
            .filter((s) => s.items > 0)
            .map((s) => (
              <Tooltip key={s.status} title={`${s.status}: ${formatCount(s.items)} items`}>
                <Box
                  sx={{
                    flex: s.items,
                    bgcolor: STATUS_COLORS[s.status] ?? C.faint,
                    borderRadius: "3px",
                    opacity: selected.length && !selected.includes(s.status) ? 0.35 : 1,
                  }}
                />
              </Tooltip>
            ))}
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 0.5, mt: total > 0 ? 0 : 1.5 }}>
        {visible.map((s) => {
          const isSelected = selected.includes(s.status);
          return (
            <ButtonBase
              key={s.status}
              onClick={() => onToggle(s.status)}
              aria-pressed={isSelected}
              sx={{
                display: "grid",
                gridTemplateColumns: "12px minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.75,
                minHeight: 44,
                borderRadius: "6px",
                textAlign: "left",
                border: `1px solid ${isSelected ? C.ink : "transparent"}`,
                bgcolor: isSelected ? C.bone1 : "transparent",
                "&:hover": { bgcolor: C.bone1 },
                "&:focus-visible": { outline: `2px solid ${C.coral}` },
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: STATUS_COLORS[s.status] ?? C.faint }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 600 }} noWrap>
                  {s.status}
                </Typography>
                <Typography variant="caption" sx={{ color: C.muted, display: "block" }} noWrap>
                  {formatCount(s.orders)} orders · {formatMoney(s.value, currency)}
                  {s.oldestAgeMs !== null && ` · oldest ${formatDuration(s.oldestAgeMs)}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatCount(s.items)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Panel>
  );
}
